import { VerityOptions } from "@verity/api-client/types";
import {
	checkExtension,
	getProctoringOptions,
	isBrowserSupported,
	isVerityLockdown,
	isVersionSupported,
	isAssessmentVerityEnabledFromInfo,
	AssessmentQueryInfo,
} from "./helpers";
import { getBrowserType, isChromeOs, isMobileDevice, isPlatformCompatible } from "../../helpers/device";
import verityLogger from "../../helpers/logger";

export default async function checkStartAssessment({
	getAssessmentInfo,
	isInstructor,
	extensionConnectIframe,
	checkVerityEnabledFallback,
	injectLoading,
	removeLoading,
	injectUnsupportedBrowser,
	injectInstallExtension,
	injectUpdateExtension,
	injectLaunchLockdown,
	injectLockdownOrMobile,
	injectLaunchMobile,
}: {
	getAssessmentInfo: () => Promise<AssessmentQueryInfo> | AssessmentQueryInfo;
	isInstructor: boolean;
	extensionConnectIframe: HTMLIFrameElement;
	checkVerityEnabledFallback?: () => Promise<boolean> | boolean;
	injectLoading?: () => void | Promise<void>;
	removeLoading?: () => void | Promise<void>;
	injectUnsupportedBrowser?: () => void | Promise<void>;
	injectInstallExtension?: (options?: VerityOptions) => void | Promise<void>;
	injectUpdateExtension?: (options?: VerityOptions) => void | Promise<void>;
	injectLaunchLockdown?: (options?: VerityOptions) => void | Promise<void>;
	injectLockdownOrMobile?: () => void | Promise<void>;
	injectLaunchMobile?: (options?: VerityOptions) => void | Promise<void>;
}) {
	const inLockdownOrMobile = await isVerityLockdown(extensionConnectIframe);
	if (!injectLockdownOrMobile && (inLockdownOrMobile || isInstructor)) {
		return;
	}
	injectLoading?.();
	try {
		const { instituteId, lmsProvidedCourseId, lmsProvidedAssessmentId, assessmentType } = await getAssessmentInfo();
		let verityEnabled: boolean;
		let assessmentProctoringOption: VerityOptions | undefined;
		if (instituteId != null && lmsProvidedCourseId && lmsProvidedAssessmentId) {
			verityEnabled = await isAssessmentVerityEnabledFromInfo({
				extensionConnectIframe,
				instituteId,
				lmsProvidedCourseId,
				lmsProvidedAssessmentId,
				assessmentType,
			});
			if (verityEnabled) {
				assessmentProctoringOption = await getProctoringOptions(
					extensionConnectIframe,
					instituteId,
					lmsProvidedCourseId,
					lmsProvidedAssessmentId,
				);
			}
		} else {
			verityEnabled = (await checkVerityEnabledFallback?.()) ?? false;
		}

		if (verityEnabled) {
			if (inLockdownOrMobile) {
				injectLockdownOrMobile?.();
				return;
			} else if (isInstructor) {
				return;
			}
			const { isExtension = true, isLockdown, isMobile } = assessmentProctoringOption ?? {};
			if (isMobile && isMobileDevice()) {
				// if verity-enabled and is mobile lockdown assessment, replace the preview button to show the mobile lockdown launch modal
				await injectLaunchMobile?.(assessmentProctoringOption);
			} else if (isBrowserSupported()) {
				const checkExtensionRes = await checkExtension(extensionConnectIframe).catch(() => {
					return { exists: false, version: undefined };
				});
				if (checkExtensionRes.exists) {
					if (
						checkExtensionRes.version &&
						!(await isVersionSupported(extensionConnectIframe, checkExtensionRes.version))
					) {
						await injectUpdateExtension?.(assessmentProctoringOption);
					}
				} else {
					const browserType = getBrowserType();
					// if verity-enabled and is extension assessment, but doesn't have the extension, show download required page (if Lockdown and no way to inject Lockdown, still show extension required so extension can inject Lockdown script)
					if (
						((isExtension || (isLockdown && isChromeOs())) && isPlatformCompatible("extension", browserType)) ||
						(isLockdown && !injectLaunchLockdown)
					) {
						await injectInstallExtension?.(assessmentProctoringOption);
					}
					// if verity-enabled and is lockdown assessment, replace the preview button to show the lockdown launch modal
					else if (isLockdown && isPlatformCompatible("lockdown", browserType)) {
						await injectLaunchLockdown?.(assessmentProctoringOption);
					}
				}
			} else {
				await injectUnsupportedBrowser?.();
			}
		}
	} catch (e) {
		verityLogger.error(e);
		throw e;
	} finally {
		await removeLoading?.();
	}
}
